import { tap, delay, forkJoin, switchMap, Observable } from 'rxjs';

import { signal, computed, Injectable } from '@angular/core';

import { Addons, Subscription, SubscriptionDetails, SubscriptionsService } from '@/app/api/subscriptions';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionsStore {
  constructor(private subscriptionsService: SubscriptionsService) {}

  private readonly _subscription = signal<Subscription | null>(null);
  private readonly _subscriptionDetails = signal<SubscriptionDetails | null>(null);
  private readonly _subscriptionAddon = signal<Addons | null>(null);

  public readonly subscription = this._subscription.asReadonly();
  public readonly subscriptionDetails = this._subscriptionDetails.asReadonly();
  public readonly subscriptionAddon = this._subscriptionAddon.asReadonly();

  public readonly isUnlimitedContentUpdates = computed(() => {
    const contentUpdatesQuota = this.subscription()?.ads_edit;
    return contentUpdatesQuota?.total_limit > 1_000_000;
  });

  public readonly isAdsLimitReached = computed(() => {
    const ads = this.subscription()?.ads;

    if (!ads) {
      return true;
    }

    return ads.remaining === 0;
  });

  public readonly isContentUpdatesLimitReached = computed(() => {
    const contentUpdatesQuota = this.subscription()?.ads_edit;

    if (!contentUpdatesQuota) {
      return true;
    }

    return contentUpdatesQuota.remaining === 0;
  });

  setSubscription(subscription: Subscription) {
    this._subscription.set(subscription);
  }

  getCurrentUserSubscription(): Observable<Subscription> {
    return this.subscriptionsService
      .getCurrentUserSubscription()
      .pipe(tap((subscription) => this.updateSubscriptions(subscription)));
  }

  getCurrentUserSubscriptionDetails(): Observable<SubscriptionDetails> {
    return this.subscriptionsService.getCurrentUserSubscriptionDetails().pipe(
      tap((res) => {
        if (res) {
          this._subscriptionDetails.set(res);
          this._subscription.set(res.subscription);
        }
      }),
    );
  }

  cancelSubscription(): Observable<Subscription> {
    return this.subscriptionsService.cancelSubscription().pipe(
      tap((subscription) => {
        this.updateSubscriptions(subscription);
      }),
    );
  }

  renewSubscription(): Observable<Subscription> {
    return this.subscriptionsService.renewSubscription().pipe(
      tap((subscription) => {
        this.updateSubscriptions(subscription);
      }),
    );
  }

  getAddonsList(): Observable<Addons[]> {
    return this.subscriptionsService.getAddonsList().pipe(tap((res) => this._subscriptionAddon.set(res[0])));
  }

  reduceAddonsAmount(addonPriceId: string, quantity: number): Observable<[Addons[], SubscriptionDetails]> {
    return this.subscriptionsService.reduceAddonsAmount(addonPriceId, quantity).pipe(
      delay(3000),
      switchMap(() => forkJoin([this.getAddonsList(), this.getCurrentUserSubscriptionDetails()])),
      tap(([addonsList, subscriptionDetails]) => {
        this.updateSubscriptions(subscriptionDetails.subscription);
      }),
    );
  }

  purchaseAddonsAmount(addonPriceId: string, quantity: number): Observable<[Addons[], SubscriptionDetails]> {
    return this.subscriptionsService.purchaseAddonsAmount(addonPriceId, quantity).pipe(
      delay(3000),
      switchMap(() => forkJoin([this.getAddonsList(), this.getCurrentUserSubscriptionDetails()])),
      tap(([addonsList, subscriptionDetails]) => {
        this.updateSubscriptions(subscriptionDetails.subscription);
      }),
    );
  }

  private updateSubscriptions(subscription: Subscription) {
    this._subscription.set(subscription);
    this._subscriptionDetails.update((subscriptionDetails) => {
      if (!subscriptionDetails) {
        return null;
      }

      return {
        ...subscriptionDetails,
        subscription: subscription,
      };
    });
  }
}
